import { useMsal } from '@azure/msal-react';
import { Button } from '@consigli/facade';
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { loginRequest } from '../config/msal-config';

interface MicrosoftAuthButtonProps {
  onAuthSuccess?: (accessToken: string) => void;
  className?: string;
}

export const MicrosoftAuthButton: React.FC<MicrosoftAuthButtonProps> = ({
  onAuthSuccess,
  className = '',
}) => {
  const { t } = useTranslation();
  const { instance, accounts } = useMsal();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = accounts.length > 0;

  const handleLogin = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await instance.loginPopup(loginRequest);

      if (onAuthSuccess && response.accessToken) {
        onAuthSuccess(response.accessToken);
      }
    } catch (error) {
      console.error('❌ Login failed:', error);
      setError(error instanceof Error ? error.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  }, [instance, onAuthSuccess]);

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      await instance.logoutPopup();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Logout failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (error) {
    return (
      <div className={`p-4 bg-red-50 border border-red-200 rounded-lg ${className}`}>
        <p className="text-red-700 text-sm">Error: {error}</p>
        <Button tertiary onClick={() => setError(null)}>
          {t('meeting-secretary.header.dismiss')}
        </Button>
      </div>
    );
  }

  return (
    <div className={className}>
      {isAuthenticated ? (
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <span className="text-sm text-gray-700">
              {t('meeting-secretary.header.signed-in-as', { username: accounts[0]?.username })}
            </span>
          </div>
          <Button primary rounded onClick={handleLogout} disabled={isLoading} className="w-full">
            {isLoading
              ? t('meeting-secretary.header.signing-out')
              : t('meeting-secretary.header.sign-out-of-microsoft')}
          </Button>
        </div>
      ) : (
        <Button primary rounded onClick={handleLogin} disabled={isLoading} className="w-full">
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>{t('meeting-secretary.header.signing-in')}</span>
            </>
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
              <span>{t('meeting-secretary.header.sign-in-with-microsoft')}</span>
            </>
          )}
        </Button>
      )}
    </div>
  );
};
